import { useMemo, useState } from 'react'
import { DatePicker, Modal, Tooltip, Select, Alert } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs, { type Dayjs } from 'dayjs'
import { Upload, Loader2, Eye } from 'lucide-react'
import { toast } from 'sonner'
import { approvalsApi, sapApi } from '@/api/services'
import { useAuthStore } from '@/stores/authStore'
import {
  extractTransporterSapRows,
  resolveFromLocation,
  resolveToLocation,
  resolveTransporterCode,
  rowKey,
  sapDateRangeToRequest,
  type TransporterSapRow,
} from '@/utils/sapTransporter'
import { getColumnSearchProps, textMatchesColumnFilter } from '@/components/table/columnSearchProps'
import { leapTablePagination } from '@/components/table/defaultPagination'
import { LeapDataTable } from '@/components/table/LeapDataTable'
import { EllipsisCell } from '@/components/table/EllipsisCell'
import { transporterRowClass } from '@/components/table/statusRowClass'
import { Button } from '@/components/ui/Button'
import CustomFileUpload from '@/components/pages/uploadPOD/uploader/CustomFileUpload'
import { useTranslation } from 'react-i18next'
import { PageShell, PageHero, DashboardPanel, FilterPanel } from '@/components/dashboard'
import { UploadCloud } from 'lucide-react'

const { RangePicker } = DatePicker

function cell(v: unknown): string {
  if (v == null || v === '') return '—'
  return String(v).trim() || '—'
}

/** Traffic-light colours for the POD lifecycle (purple = needs upload). */
const POD_STATUS_COLORS = {
  toUpload: '#a855f7', // purple
  Pending: '#eab308', // yellow
  Approved: '#22c55e', // green
  Rejected: '#ef4444', // red
} as const

function Dot({ color, title }: { color: string; title?: string }) {
  return (
    <span
      aria-hidden
      title={title}
      style={{ backgroundColor: color }}
      className="inline-block size-2.5 shrink-0 rounded-full"
    />
  )
}

/**
 * Row tint so the table is scannable at a glance (see transporterRowClass in statusRowClass.ts).
 */
function rowStatusClass(status: keyof typeof POD_STATUS_COLORS): string {
  return transporterRowClass(status === 'toUpload' ? null : status)
}

/** Cumulative POD journey: purple persists and each stage adds its dot. */
function statusStages(status: keyof typeof POD_STATUS_COLORS): Array<keyof typeof POD_STATUS_COLORS> {
  switch (status) {
    case 'Approved':
      return ['toUpload', 'Pending', 'Approved']
    case 'Rejected':
      return ['toUpload', 'Pending', 'Rejected']
    case 'Pending':
      return ['toUpload', 'Pending']
    default:
      return ['toUpload']
  }
}

export default function TransporterPendingUploadPage() {
  const queryClient = useQueryClient()
  const { user } = useAuthStore()
  const transporterCode = resolveTransporterCode(user)
  const { t } = useTranslation(['Sidebar', 'UploadPOD', 'ReportsPOD', 'Common', 'PendingPOD'])

  const allowedTransporters = useMemo(() => {
    return Array.isArray(user?.transporters) ? user.transporters : []
  }, [user?.transporters])

  const [activeTransporterCode, setActiveTransporterCode] = useState<string>(() => {
    return allowedTransporters[0]?.code || transporterCode || ''
  })

  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().subtract(3, 'month'),
    dayjs(),
  ])
  const [uploadOpen, setUploadOpen] = useState(false)
  const [selectedRow, setSelectedRow] = useState<TransporterSapRow | null>(null)
  const [sapDetail, setSapDetail] = useState<Record<string, unknown> | null>(null)
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([])
  const [fileInputKey, setFileInputKey] = useState(() => Date.now())

  const { fromDate, toDate } = sapDateRangeToRequest(dateRange)

  const {
    data: rows = [],
    isPending,
    isFetching,
    error,
    refetch,
  } = useQuery({
    queryKey: ['transporter-sap-docs', activeTransporterCode, fromDate, toDate],
    queryFn: async () => {
      const res = await sapApi.fetchTransporterDocumentsLegacy({
        Request_Data: {
          DocType: '',
          TransporterCode: activeTransporterCode,
          FromDate: fromDate,
          ToDate: toDate,
        },
      })
      const body = res.data
      if (body && typeof body === 'object' && (body as { success?: boolean }).success === false) {
        throw new Error((body as { error?: string }).error || t('UploadPOD:messages.fetchFailed', 'Failed to fetch document from SAP'))
      }
      return extractTransporterSapRows(body)
    },
    enabled: Boolean(activeTransporterCode),
    staleTime: 30_000,
  })

  const fetchDocMutation = useMutation({
    mutationFn: (row: TransporterSapRow) =>
      sapApi.getDocumentLegacy({
        Request_Data: {
          DocNo: String(row.DocumentNo ?? ''),
          DocType: String(row.DocType ?? ''),
        },
      }),
    onSuccess: (axiosRes) => {
      const body = axiosRes.data as Record<string, unknown> | undefined
      const dataBlock = body?.data as Record<string, unknown> | undefined
      const rd =
        (dataBlock?.Response_Data as Record<string, unknown> | undefined) ??
        ((dataBlock?.data as Record<string, unknown> | undefined)?.Response_Data as
          | Record<string, unknown>
          | undefined)
      if (rd && typeof rd === 'object') {
        setSapDetail(rd as Record<string, unknown>)
      } else {
        setSapDetail(null)
        toast.error(t('UploadPOD:messages.noDetails', 'SAP did not return document details.'))
      }
    },
    onError: (e: { message?: string }) => {
      toast.error(e?.message || t('UploadPOD:messages.fetchFailed', 'Failed to fetch document from SAP'))
    },
  })

  const uploadMutation = useMutation({
    mutationFn: approvalsApi.createApproval,
    onSuccess: () => {
      toast.success(t('UploadPOD:messages.uploadSuccess', 'Document uploaded successfully!'))
      queryClient.invalidateQueries({ queryKey: ['transporter-sap-docs'] })
      closeUploadModal()
      void refetch()
    },
    onError: (e: { response?: { data?: { error?: string } }; message?: string }) => {
      toast.error(
        e?.response?.data?.error || e?.message || t('UploadPOD:messages.uploadFailed', 'Upload failed')
      )
    },
  })

  const closeUploadModal = () => {
    setUploadOpen(false)
    setSelectedRow(null)
    setSapDetail(null)
    setUploadedFiles([])
    setFileInputKey(Date.now())
  }

  const openUpload = (row: TransporterSapRow) => {
    setSelectedRow(row)
    setSapDetail(null)
    setUploadedFiles([])
    setFileInputKey(Date.now())
    setUploadOpen(true)
    fetchDocMutation.mutate(row)
  }

  const handleViewUpload = (row: TransporterSapRow) => {
    const url = row.attachmentUrl
    if (!url) {
      toast.error(t('PendingPOD:messages.noAttachmentAvailable', 'No attachment available'))
      return
    }
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  const mergeFilesToPdf = async (files: File[]) => {
    const { PDFDocument } = await import('pdf-lib')
    const pdfDoc = await PDFDocument.create()
    for (const file of files) {
      const ext = file.name.split('.').pop()?.toLowerCase()
      if (!ext || !['jpg', 'jpeg', 'png', 'pdf'].includes(ext)) continue
      const buf = await file.arrayBuffer()
      if (ext === 'png') {
        const img = await pdfDoc.embedPng(buf)
        const page = pdfDoc.addPage([img.width, img.height])
        page.drawImage(img, { x: 0, y: 0, width: img.width, height: img.height })
      } else if (ext === 'jpg' || ext === 'jpeg') {
        const img = await pdfDoc.embedJpg(buf)
        const page = pdfDoc.addPage([img.width, img.height])
        page.drawImage(img, { x: 0, y: 0, width: img.width, height: img.height })
      } else {
        const src = await PDFDocument.load(buf)
        const pages = await pdfDoc.copyPages(src, src.getPageIndices())
        pages.forEach((p) => pdfDoc.addPage(p))
      }
    }
    const merged = await pdfDoc.save()
    return new File([new Uint8Array(merged)], `merged_${Date.now()}.pdf`, {
      type: 'application/pdf',
    })
  }

  const buildFormData = (mergedFile: File) => {
    const d = sapDetail || selectedRow || {}
    const materialDocNo =
      (d.MaterialCodeNo as string) ||
      (d.MaterialDocNo as string) ||
      (d.materialCodeNo as string) ||
      ''
    const lrNo = (d.LRNo as string) || (d.LR_No as string) || (d.LR_Number as string) || ''
    const doctype =
      (d.DocType as string) ||
      (d.Type as string) ||
      (selectedRow?.DocType as string) ||
      ''
    const { code: fromCode } = resolveFromLocation(d as Record<string, unknown>)
    const { code: toCode } = resolveToLocation(d as Record<string, unknown>)
    const docNo = (d.DocumentNo as string) || (d.DocNo as string) || selectedRow?.DocumentNo || ''

    const fd = new FormData()
    fd.append('year', String(d.Year ?? ''))
    fd.append('postingDate', String(d.PostingDate ?? ''))
    fd.append('fromLocation', String(fromCode))
    fd.append('toLocation', String(toCode))
    fd.append('status', 'Pending')
    fd.append('file', mergedFile, mergedFile.name)
    fd.append('type', String(doctype))
    fd.append('documentNo', String(docNo))
    fd.append('materialCodeNo', String(materialDocNo))
    fd.append('quantity', String((d.Quantity ?? '').toString().trim()))
    fd.append('vehicleNo', String(d.VehicleNo ?? ''))
    fd.append('lrNo', lrNo)
    fd.append('customerCode', String(d.CustomerCode ?? ''))
    fd.append('customerName', String(d.CustomerName ?? ''))
    fd.append(
      'transporterCode',
      String(d.Transporter ?? d.TransporterCode ?? transporterCode ?? '')
    )
    fd.append('transporterName', String(d.TransporterName ?? ''))
    fd.append('app', 'LEAP')
    fd.append('sapResponseJson', JSON.stringify(d))
    return fd
  }

  const handleUploadSubmit = async () => {
    if (!uploadedFiles.length) {
      Modal.error({
        title: t('UploadPOD:errors.noFilesTitle', 'No files'),
        content: t('UploadPOD:errors.noFilesContent', 'Please upload at least one file.')
      })
      return
    }
    if (!sapDetail && !selectedRow) {
      Modal.error({
        title: t('UploadPOD:errors.noDocTitle', 'No Document'),
        content: t('UploadPOD:errors.noDocContent', 'Please fetch a document from SAP first.')
      })
      return
    }
    try {
      const merged = await mergeFilesToPdf(uploadedFiles)
      if (merged.size / (1024 * 1024) > 25) {
        Modal.error({
          title: t('UploadPOD:errors.fileSizeTitle', 'File Size Error'),
          content: t('UploadPOD:errors.fileSizeContent', 'Attachments cannot exceed 25 MB.')
        })
        return
      }
      uploadMutation.mutate(buildFormData(merged))
    } catch {
      Modal.error({
        title: t('UploadPOD:errors.mergeFailedTitle', 'Merge failed'),
        content: t('UploadPOD:errors.mergeFailedContent', 'Could not merge attachments into PDF.')
      })
    }
  }

  const rejectedDocNos = useMemo(() => {
    const set = new Set<string>()
    rows.forEach((r) => {
      if ((r.podStatus ?? 'toUpload') === 'Rejected' && r.DocumentNo) {
        set.add(String(r.DocumentNo).trim())
      }
    })
    return set
  }, [rows])

  const columns: ColumnsType<TransporterSapRow> = useMemo(
    () => [
      {
        title: t('ReportsPOD:columns.documentNo', 'Document No'),
        dataIndex: 'DocumentNo',
        key: 'DocumentNo',
        fixed: 'left',
        width: 120,
        render: (v) => <span className="font-semibold">{cell(v)}</span>,
        ...getColumnSearchProps(t('ReportsPOD:columns.documentNo', 'Document No'), (value, record) =>
          textMatchesColumnFilter(value, record.DocumentNo)
        ),
      },
      {
        title: t('ReportsPOD:columns.docType', 'Doc Type'),
        dataIndex: 'DocType',
        key: 'DocType',
        width: 150,
        filters: [
          { text: 'Allot(ALT)', value: 'ALT' },
          { text: 'Dehire(DHR)', value: 'DHR' },
          { text: 'Goods Receipt Note(GRN)', value: 'GRN' },
          { text: 'Relocation(RELO)', value: 'REL_OUT' },
          { text: 'Trading Sale(TS)', value: 'TS' },
        ],
        onFilter: (value, record) => {
          return String(record.DocType ?? '') === value
        },
      },
      {
        title: t('UploadPOD:status.columnTitle', 'POD Status'),
        key: 'podStatus',
        width: 150,
        filters: [
          { text: t('UploadPOD:status.toUpload', 'To upload'), value: 'toUpload' },
          { text: t('UploadPOD:status.pending', 'Pending'), value: 'Pending' },
          { text: t('UploadPOD:status.approved', 'Approved'), value: 'Approved' },
          { text: t('UploadPOD:status.rejected', 'Rejected'), value: 'Rejected' },
        ],
        onFilter: (value, record) => {
          const status = record.podStatus ?? 'toUpload'
          if (value === 'Rejected') {
            const docNo = String(record.DocumentNo ?? '').trim()
            return status === 'Rejected' || (docNo ? rejectedDocNos.has(docNo) : false)
          }
          return status === value
        },
        render: (_, record) => {
          // SAP rows fold in the active status (null => to upload); rejected
          // attachments arrive as their own rows with podStatus 'Rejected'.
          const status = (record.podStatus ?? 'toUpload') as keyof typeof POD_STATUS_COLORS
          const labels: Record<keyof typeof POD_STATUS_COLORS, string> = {
            toUpload: t('UploadPOD:status.toUpload', 'To upload'),
            Pending: t('UploadPOD:status.pending', 'Pending'),
            Approved: t('UploadPOD:status.approved', 'Approved'),
            Rejected: t('UploadPOD:status.rejected', 'Rejected'),
          }
          const rejectionText = record.rejection_remark || record.remarks || record.latestRejectionRemark
          const tooltipContent = status === 'Rejected' && rejectionText
            ? `${labels[status]}: ${rejectionText}`
            : labels[status]
          return (
            <Tooltip title={tooltipContent}>
              <span className="inline-flex items-center gap-2 cursor-default">
                <span className="inline-flex items-center gap-1">
                  {statusStages(status).map((s) => (
                    <Dot key={s} color={POD_STATUS_COLORS[s]} title={labels[s]} />
                  ))}
                </span>
                <span className="text-xs">{labels[status]}</span>
              </span>
            </Tooltip>
          )
        },
      },
      {
        title: t('ReportsPOD:columns.postingDate', 'Posting Date'),
        dataIndex: 'PostingDate',
        key: 'PostingDate',
        width: 120,
        render: (v) => cell(v),
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
          <div style={{ padding: 8 }}>
            <DatePicker
              value={
                selectedKeys[0]
                  ? (() => {
                      const parts = String(selectedKeys[0]).split('/')
                      return dayjs()
                        .year(parseInt(parts[2], 10))
                        .month(parseInt(parts[1], 10) - 1)
                        .date(parseInt(parts[0], 10))
                    })()
                  : null
              }
              onChange={(date) => {
                setSelectedKeys(date ? [date.format('DD/MM/YYYY')] : [])
                confirm()
              }}
              format="YYYY/MM/DD"
              placeholder="Select date"
              style={{ width: 150 }}
            />
            {Boolean(selectedKeys[0]) && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  clearFilters?.()
                  confirm()
                }}
                className="mt-2 w-full text-xs"
              >
                Reset
              </Button>
            )}
          </div>
        ),
        onFilter: (value, record) => {
          if (!value || !record.PostingDate) return false
          const fParts = String(value).split('/')
          if (fParts.length !== 3) return false
          const fd = parseInt(fParts[0], 10)
          const fm = parseInt(fParts[1], 10) - 1
          const fy = parseInt(fParts[2], 10)

          const rStr = String(record.PostingDate).trim()
          const rParts = rStr.split(/[-/.]/)
          if (rParts.length === 3) {
            let rd, rm, ry
            if (rParts[0].length === 4) {
              ry = parseInt(rParts[0], 10)
              rm = parseInt(rParts[1], 10) - 1
              rd = parseInt(rParts[2], 10)
            } else {
              rd = parseInt(rParts[0], 10)
              rm = parseInt(rParts[1], 10) - 1
              ry = parseInt(rParts[2], 10)
            }
            return rd === fd && rm === fm && ry === fy
          }
          return false
        },
      },
      {
        title: t('ReportsPOD:columns.customerCode', 'Customer Code'),
        dataIndex: 'CustomerCode',
        key: 'CustomerCode',
        width: 120,
        render: (v) => cell(v),
        ...getColumnSearchProps(t('ReportsPOD:columns.customerCode', 'Customer Code'), (value, record) =>
          textMatchesColumnFilter(value, record.CustomerCode)
        ),
      },
      {
        title: t('ReportsPOD:columns.vendorCode', 'Vendor Code'),
        key: 'VendorCode',
        width: 120,
        render: (_, record) => {
          const v = record.VendorCode ?? record.Vendor_Code ?? record.Lifnr
          return cell(v)
        },
        ...getColumnSearchProps(t('ReportsPOD:columns.vendorCode', 'Vendor Code'), (value, record) => {
          const v = record.VendorCode ?? record.Vendor_Code ?? record.Lifnr
          return textMatchesColumnFilter(value, v)
        }),
      },
      {
        title: t('ReportsPOD:columns.vendorName', 'Vendor Name'),
        key: 'VendorName',
        width: 180,
        ellipsis: true,
        render: (_, record) => {
          const v = record.VendorName ?? record.Vendor_Name ?? record.Vendor
          return <EllipsisCell>{cell(v)}</EllipsisCell>
        },
        ...getColumnSearchProps(t('ReportsPOD:columns.vendorName', 'Vendor Name'), (value, record) => {
          const v = record.VendorName ?? record.Vendor_Name ?? record.Vendor
          return textMatchesColumnFilter(value, v)
        }),
      },
      {
        title: t('ReportsPOD:columns.transporterName', 'Transporter Name'),
        dataIndex: 'TransporterName',
        key: 'TransporterName',
        width: 140,
        ellipsis: true,
        render: (v, r) => cell(v ?? r.TransporterCode),
      },
      {
        title: t('ReportsPOD:columns.fromLocationCode', 'From Location Code'),
        key: 'FromLocationCode',
        width: 130,
        render: (_, record) => {
          const { code } = resolveFromLocation(record)
          return cell(code)
        },
        ...getColumnSearchProps(t('ReportsPOD:columns.fromLocationCode', 'From Location Code'), (value, record) => {
          const { code } = resolveFromLocation(record)
          return textMatchesColumnFilter(value, code)
        }),
      },
      {
        title: t('ReportsPOD:columns.fromLocation', 'From Location'),
        key: 'FromLocationName',
        width: 150,
        ellipsis: true,
        render: (_, record) => {
          const { name } = resolveFromLocation(record)
          return <EllipsisCell>{cell(name)}</EllipsisCell>
        },
        ...getColumnSearchProps(t('ReportsPOD:columns.fromLocation', 'From Location'), (value, record) => {
          const { name } = resolveFromLocation(record)
          return textMatchesColumnFilter(value, name)
        }),
      },
      {
        title: t('ReportsPOD:columns.toLocationCode', 'To Location Code'),
        key: 'ToLocationCode',
        width: 130,
        render: (_, record) => {
          const { code } = resolveToLocation(record)
          return cell(code)
        },
        ...getColumnSearchProps(t('ReportsPOD:columns.toLocationCode', 'To Location Code'), (value, record) => {
          const { code } = resolveToLocation(record)
          return textMatchesColumnFilter(value, code)
        }),
      },
      {
        title: t('ReportsPOD:columns.toLocation', 'To Location'),
        key: 'ToLocationName',
        width: 150,
        ellipsis: true,
        render: (_, record) => {
          const { name } = resolveToLocation(record)
          return <EllipsisCell>{cell(name)}</EllipsisCell>
        },
        ...getColumnSearchProps(t('ReportsPOD:columns.toLocation', 'To Location'), (value, record) => {
          const { name } = resolveToLocation(record)
          return textMatchesColumnFilter(value, name)
        }),
      },
      {
        title: t('ReportsPOD:columns.lrNo', 'LR Number'),
        dataIndex: 'LR_Number',
        key: 'LR_Number',
        width: 130,
        render: (v, r) => cell(v ?? r.LR_No),
        ...getColumnSearchProps(t('ReportsPOD:columns.lrNo', 'LR Number'), (value, record) =>
          textMatchesColumnFilter(value, record.LR_Number ?? record.LR_No)
        ),
      },
      {
        title: t('ReportsPOD:columns.quantity', 'Quantity'),
        dataIndex: 'Quantity',
        key: 'Quantity',
        width: 80,
        render: (v) => cell(v),
      },
      {
        title: t('ReportsPOD:details.vehicleNo', 'Vehicle'),
        dataIndex: 'VehicleNo',
        key: 'VehicleNo',
        width: 100,
        render: (v) => cell(v),
        ...getColumnSearchProps(t('ReportsPOD:details.vehicleNo', 'Vehicle'), (value, record) =>
          textMatchesColumnFilter(value, record.VehicleNo)
        ),
      },
      {
        title: t('ReportsPOD:columns.remarks', 'Remarks'),
        key: 'remarks',
        width: 180,
        ellipsis: true,
        render: (_, record) => {
          const remark =
            record.rejection_remark ||
            record.remarks ||
            (record.podStatus === 'Rejected' ? record.latestRejectionRemark : null)
          if (!remark) return <span className="text-muted-foreground">—</span>
          const isRejected = record.podStatus === 'Rejected'
          return (
            <Tooltip title={remark}>
              <span className={isRejected ? 'text-xs text-red-600 font-medium' : 'text-xs text-muted-foreground'}>
                <EllipsisCell>{remark}</EllipsisCell>
              </span>
            </Tooltip>
          )
        },
        ...getColumnSearchProps(t('ReportsPOD:columns.remarks', 'Remarks'), (value, record) => {
          const remark =
            record.rejection_remark ||
            record.remarks ||
            (record.podStatus === 'Rejected' ? record.latestRejectionRemark : null)
          return textMatchesColumnFilter(value, remark)
        }),
      },
      {
        title: t('ReportsPOD:columns.actions', 'Actions'),
        key: 'action',
        fixed: 'right',
        width: 88,
        className: 'leap-table-actions-col',
        onCell: () => ({ className: 'leap-table-actions-cell' }),
        render: (_, record) => {
          const uploadLabel = t('ReportsPOD:columns.upload', 'Upload')
          const viewLabel = t('PendingPOD:actions.actionView', 'View')
          const canUpload = record.rowType === 'sap' && record.canUpload

          return (
            <div className="leap-table-actions">
              {canUpload ? (
                <Tooltip title={uploadLabel}>
                  <button
                    type="button"
                    className="leap-table-action-btn"
                    aria-label={uploadLabel}
                    onClick={() => openUpload(record)}
                  >
                    <Upload className="size-3.5" />
                  </button>
                </Tooltip>
              ) : (
                <Tooltip title={viewLabel}>
                  <button
                    type="button"
                    className="leap-table-action-btn"
                    aria-label={viewLabel}
                    onClick={() => handleViewUpload(record)}
                  >
                    <Eye className="size-3.5" />
                  </button>
                </Tooltip>
              )}
            </div>
          )
        },
      },
    ],
    [t, transporterCode, rejectedDocNos]
  )

  const detailEntries = useMemo(() => {
    const src = sapDetail || selectedRow
    if (!src || typeof src !== 'object') return []
    return Object.entries(src)
      .filter(([k]) => !/sapResponse/i.test(k))
      .sort(([a], [b]) => a.localeCompare(b))
  }, [sapDetail, selectedRow])

  if (!activeTransporterCode) {
    return (
      <PageShell>
        <PageHero
          title={t('UploadPOD:transporterSetup.title', 'Transporter setup required')}
          subtitle={t(
            'UploadPOD:transporterSetup.description',
            'Your LEAP profile has the POD Transporter role but no company / BP (transporter) code. Ask an admin to assign your company code in IAM (same value SAP uses as TransporterCode).',
          )}
        />
      </PageShell>
    )
  }

  return (
    <PageShell>
      <PageHero
        title={t('Sidebar:menu.pendingUploadTransporter', 'Pending PODs for upload')}
        subtitle={t('UploadPOD:transporter.subtitle', 'SAP documents for transporter code {{code}} (last 3 months by default)', {
          code: activeTransporterCode,
        })}
        badges={[{ label: 'Transporter', accent: true, icon: UploadCloud }]}
        actions={
          <div className="flex items-center gap-3">
            {allowedTransporters.length > 1 && (
              <Select
                value={activeTransporterCode}
                onChange={setActiveTransporterCode}
                style={{ width: 220 }}
                options={allowedTransporters.map((item) => ({
                  value: item.code,
                  label: `${item.name} (${item.code})`,
                }))}
              />
            )}
            <RangePicker
              value={dateRange}
              onChange={(v) => v && setDateRange(v as [Dayjs, Dayjs])}
              allowClear={false}
              format="DD/MM/YYYY"
            />
          </div>
        }
      />

      {error ? (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {(error as Error).message || t('UploadPOD:messages.fetchFailed', 'Failed to fetch document from SAP')}
        </div>
      ) : null}

      <FilterPanel label="Status legend">
        <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-2">
          <Dot color={POD_STATUS_COLORS.toUpload} />
          {t('UploadPOD:status.toUpload', 'To upload')}
        </span>
        <span className="inline-flex items-center gap-2">
          <Dot color={POD_STATUS_COLORS.Pending} />
          {t('UploadPOD:status.pending', 'Pending')}
        </span>
        <span className="inline-flex items-center gap-2">
          <Dot color={POD_STATUS_COLORS.Approved} />
          {t('UploadPOD:status.approved', 'Approved')}
        </span>
        <span className="inline-flex items-center gap-2">
          <Dot color={POD_STATUS_COLORS.Rejected} />
          {t('UploadPOD:status.rejected', 'Rejected')}
        </span>
        </div>
      </FilterPanel>

      <DashboardPanel flush className="leap-data-page-card">
        <LeapDataTable<TransporterSapRow>
          rowKey={(r, i) =>
            r.rowType === 'upload' && r.approvalId != null ? `upload-${r.approvalId}` : `sap-${rowKey(r, i ?? 0)}`
          }
          columns={columns}
          dataSource={rows}
          loading={isPending || isFetching}
          rowClassName={record => rowStatusClass((record.podStatus ?? 'toUpload') as keyof typeof POD_STATUS_COLORS)}
          pagination={leapTablePagination(rows.length)}
        />
      </DashboardPanel>

      <Modal
        title={t('UploadPOD:header.title', 'Upload POD')}
        open={uploadOpen}
        onCancel={closeUploadModal}
        width="min(96vw, 960px)"
        footer={[
          <Button key="cancel" type="button" variant="outline" onClick={closeUploadModal}>
            {t('Common:general.cancel', 'Cancel')}
          </Button>,
          <Button
            key="ok"
            type="button"
            onClick={() => void handleUploadSubmit()}
            disabled={uploadMutation.isPending || fetchDocMutation.isPending}
          >
            {uploadMutation.isPending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Upload className="size-4" />
            )}
            {t('UploadPOD:upload.submit', 'Upload')}
          </Button>,
        ]}
      >
        {fetchDocMutation.isPending ? (
          <div className="py-8 text-center text-gray-500">{t('UploadPOD:form.fetching', 'Fetching…')}</div>
        ) : (
          <>
            {(selectedRow?.rejection_remark || selectedRow?.latestRejectionRemark) ? (
              <Alert
                type="error"
                showIcon
                className="mb-4 border-red-200 bg-red-50 text-red-900"
                message={
                  <div className="space-y-0.5">
                    <span className="font-semibold text-xs text-red-800 uppercase tracking-wide">
                      {t('PendingPOD:table.rejectionReason', 'Rejection Reason')}:
                    </span>
                    <p className="text-sm font-medium text-red-700">
                      {selectedRow?.rejection_remark || selectedRow?.latestRejectionRemark}
                    </p>
                  </div>
                }
              />
            ) : null}
            <p className="mb-3 text-sm font-semibold text-gray-700">{t('UploadPOD:details.title', 'Fetched Details')}</p>
            <div className="mb-4 grid max-h-48 grid-cols-2 gap-2 overflow-y-auto sm:grid-cols-4">
              {detailEntries.slice(0, 24).map(([k, v]) => (
                <div key={k} className="rounded-lg bg-gray-50 px-2 py-1.5 text-xs">
                  <div className="text-gray-500">{k}</div>
                  <div className="font-medium text-gray-900">{cell(v)}</div>
                </div>
              ))}
            </div>
            <CustomFileUpload
              key={fileInputKey}
              uploadedFiles={uploadedFiles}
              setUploadedFiles={setUploadedFiles}
            />
          </>
        )}
      </Modal>
    </PageShell>
  )
}
